import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { RepositoryBackend } from './data/repository-backend';
import { FallbackTranslationRepository } from './data/fallback-translation-repository';
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  NAMESPACES,
  DEFAULT_NAMESPACE,
} from './types/i18n-types';

/**
 * Create repository instance
 * Uses FallbackTranslationRepository which:
 * - Primary: Fetches from API via getAllTranslationsUseCase
 * - Fallback: Uses local JSON files if API fails
 */
const translationRepository = new FallbackTranslationRepository();

/**
 * Create i18next backend adapter that uses our repository
 */
const repositoryBackend = RepositoryBackend.create(translationRepository);

/**
 * Initialize i18next with react-i18next
 */
i18n
  .use(repositoryBackend)
  .use(initReactI18next)
  .init({
    // Language settings
    lng: DEFAULT_LANGUAGE,
    fallbackLng: FALLBACK_LANGUAGE,

    // Namespace configuration
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,

    // Only load specific namespaces (on-demand loading)
    partialBundledLanguages: true,

    // Debugging (only in development)
    debug: import.meta.env.DEV,

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
      formatSeparator: ',',
    },

    // React-specific settings
    react: {
      useSuspense: true, // Use Suspense to wait for translations before rendering
      bindI18n: 'languageChanged loaded', // Events to trigger re-render
      bindI18nStore: 'added removed', // Store events to trigger re-render
    },

    // Backend options (for our custom backend)
    backend: {},

    // Missing key handling
    saveMissing: import.meta.env.DEV, // Log missing keys in dev
    missingKeyHandler: (_lngs, ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing translation key: ${ns}:${key}`);
      }
    },

    // Return empty string for missing keys in production
    returnEmptyString: false,
    returnNull: false,
  });

// Export configured i18n instance
export default i18n;

// Export repository for testing/debugging
export { translationRepository };
